import type { ExpandStatus } from "@stryker/core/types/expandable";
import type {
  AdditionalJobDetailsData,
  AdditionalJobDetailsSection,
  ChecksBaseData,
  ChecksTeeData,
  ContactInformationData,
  ContactInformationSection,
  CreateUpdateMechanicalJobSections,
  DefectDetailsData,
  DefectDetailsSection,
  DesignConditionsData,
  DesignConditionsSection,
  DrawingNotesData,
  EnclosureDetailsBaseData,
  EnclosureDetailsFlangeData,
  JointEfficiencyData,
  JointEfficiencySection,
  MechanicalExpandableCards,
  MechanicalJobDetailsData,
  PostConstructionTestingRequirementsData,
  RepairTypeData,
  SealingDetailsData,
  StrongbackMaterialData,
  StrongbackTypeLocationData,
} from "@stryker/core/types/mechanicalJobs";
import type { ComponentDetailsData } from "@stryker/core/types/mechanicalSections";
import type {
  FlangeClampDefectDetailsData,
  LongRadius90DefectDetailsData,
  StraightLineDefectDetailsData,
  TeeDefectDetailsData,
} from "@stryker/core/types/sections";
import { create } from "zustand";

// Initial defect details for straight line repair type
// Contains total_defect_length and desired_overall_length with their respective units
const initialStraightLineDefectDetails: StraightLineDefectDetailsData = {
  defects: [
    {
      total_defect_length: null,
      unit_for_total_defect_length: "Inches",
      desired_overall_length: null,
      unit_for_desired_overall_length: "Inches",
    },
  ],
  repair_length_options: [],
  repair_length_repair_kit_sizes: [],
};

// Initial defect details for long radius 90 repair type
// Contains weld_thickness_1/2 and passed_weld_1/2 (Top/Bottom) with their respective units (no arrays)
const initialLongRadius90DefectDetails: LongRadius90DefectDetailsData = {
  weld_thickness_1: null,
  unit_for_weld_thickness_1: "Inches",
  passed_weld_1: null,
  unit_for_passed_weld_1: "Inches",
  weld_thickness_2: null,
  unit_for_weld_thickness_2: "Inches",
  passed_weld_2: null,
  unit_for_passed_weld_2: "Inches",
  repair_length_options: [],
  repair_length_repair_kit_sizes: [],
};

// Initial defect details for tee repair type
// Contains fixed array of 2 defects with units inside each defect object
// First defect: defect_length_1, desired_overall_length_header (or the asymmetrical
// distance_from_center_of_riser_left/right pair) + units
// Second defect: defect_length_2, passed_header + units
const initialTeeDefectDetails: TeeDefectDetailsData = {
  defects: [
    {
      defect_length_1: null,
      desired_overall_length_header: null,
      unit_for_defect_length_1: "Inches",
      unit_for_desired_overall_length_header: "Inches",
      distance_from_center_of_riser_left: null,
      distance_from_center_of_riser_right: null,
      unit_for_distance_from_center_of_riser_left: "Inches",
      unit_for_distance_from_center_of_riser_right: "Inches",
    },
    {
      defect_length_2: null,
      passed_header: null,
      unit_for_defect_length_2: "Inches",
      unit_for_passed_header: "Inches",
    },
  ],
  repair_length_options: [],
  repair_length_repair_kit_sizes: [],
};

// Initial defect details for flange clamp repair type
// Contains thickness_gap with its unit
const initialFlangeClampDefectDetails: FlangeClampDefectDetailsData = {
  thickness_gap: null,
  unit_for_thickness_gap: "Inches",
  repair_length_options: [],
  repair_length_repair_kit_sizes: [],
};

const getInitialDefectDetails = (repairType: string | undefined) => {
  switch (repairType) {
    case "straight_line":
      return initialStraightLineDefectDetails;
    case "long_radius":
      return initialLongRadius90DefectDetails;
    case "tee":
      return initialTeeDefectDetails;
    case "flange_clamp":
      return initialFlangeClampDefectDetails;
    default:
      return initialStraightLineDefectDetails;
  }
};
const initialState = {
  jobIdForDuplication: null,
  sections: {
    jobDetails: {
      status: "open",
      isDataFilled: false,
      data: {
        branch: "",
        job_number: null,
        customer_number: "",
        plant_name: "",
        service_name: "",
        design_code: null,
      },
    },
    additionalJobDetails: {
      status: "blocked",
      isDataFilled: false,
      addMore: false,
      data: {
        unit: "",
        line_id: null,
      },
    },
    contactInformation: {
      status: "blocked",
      isDataFilled: false,
      addMore: false,
      data: {
        contact_info_name: "",
        contact_info_phone: "",
        contact_info_email: "",
      },
    },
    designConditions: {
      status: "blocked",
      isDataFilled: false,
      data: {
        design_pressure: null,
        design_temperature: null,
        unit_for_design_temperature: "Fahrenheit",
        injection_pressure_pct_increase: null,
      },
    },
    repairType: {
      status: "blocked",
      isDataFilled: false,
      data: {
        repairType: undefined,
      },
    },
    enclosureDetails: {
      status: "blocked",
      isDataFilled: false,
      data: {
        shells: "",
        endplates: "",
        ears: "",
        stud_bolts: "",
        riser_shells: "",
        match_enclosure_to_component_material: undefined,
      },
    },
    strongbacks: {
      status: "blocked",
      isDataFilled: false,
      addMore: false,
      data: {
        strongbacks: "",
        strongbacks_stud_bolts: "",
        match_strongback_to_enclosure: undefined,
      },
    },
    strongbackTypeLocation: {
      status: "blocked",
      isDataFilled: false,
      data: {
        strongback_header: undefined,
        strongback_riser: undefined,
        match_strongback_to_enclosure: undefined,
        strongbacks: [
          {
            type: "",
            distance: undefined,
            unit_for_distance: "Inches",
            flange_nps: "",
            flange_rating: "",
          },
          {
            type: "",
            distance: undefined,
            unit_for_distance: "Inches",
            flange_nps: "",
            flange_rating: "",
          },
          {
            type: "",
            distance: undefined,
            unit_for_distance: "Inches",
            flange_nps: "",
            flange_rating: "",
          },
          {
            type: "",
            distance: undefined,
            unit_for_distance: "Inches",
            flange_nps: "",
            flange_rating: "",
          },
        ],
      },
    },
    postConstructionTestingRequirements: {
      status: "blocked",
      isDataFilled: false,
      data: {
        post_construction_testing_requirements: [],
      },
    },
    sealingDetails: {
      status: "blocked",
      isDataFilled: false,
      data: {
        seal_preference: "",
        seal_type: "",
        seal_size: "",
        seal_material: "",
        sealant: "",
        sealant_2: "",
      },
    },
    drawingNotes: {
      status: "blocked",
      isDataFilled: false,
      data: {
        additional_notes: [],
        new_notes: [],
      },
    },
    checks: {
      status: "blocked",
      isDataFilled: false,
      data: {
        is_the_service_hf_acid: undefined,
        is_the_service_wet_h2s: undefined,
        is_this_a_flare_line: undefined,
        does_the_line_vibrate_or_hammer: undefined,
        do_you_want_to_add_external_pressure_calculation: undefined,
        do_you_want_to_have_small_cross_section: undefined,
        do_you_need_minimum_clearance: undefined,
        min_id_clearance: null,
        do_you_want_to_add_mdmt_to_package: undefined,
        mdmt: null,
        is_this_a_redesign: undefined,
        is_this_revision: undefined,
        revision: null,
        do_you_wish_to_change_default_joint_efficiency: undefined,
        do_you_wish_to_change_default_checked_by: undefined,
        checked_by_first_name: null,
        checked_by_last_name: null,
      },
    },
    jointEfficiency: {
      status: "blocked",
      isDataFilled: false,
      data: {
        joint_efficiency: null,
        mill_tolerance: null,
        corrosion_allowance: null,
      },
    },
    componentDetails: {
      status: "blocked",
      isDataFilled: false,
      data: {
        component_options: [],
        component_material: null,
        components_data: [
          {
            component_nps: null,
            component_diameter: null,
            unit_for_component_diameter: null,
            component_thickness: null,
            unit_for_component_thickness: null,
            component_schedule: null,
          },
        ],
      },
    },
    defectDetails: {
      status: "blocked",
      isDataFilled: false,
      data: getInitialDefectDetails(undefined),
    },
    notes: {
      status: "canBeOpened",
      isDataFilled: false,
      data: { additional_notes: [], new_notes: [] },
    },
    finalDesign: {
      status: "blocked",
      isDataFilled: false,
      data: {},
    },
  },
} satisfies { sections: CreateUpdateMechanicalJobSections; jobIdForDuplication: null | string };

export type CUMechanicalJobStore = {
  jobIdForDuplication: null | string;
  sections: CreateUpdateMechanicalJobSections;
  setJobDetails: (payload: { data: MechanicalJobDetailsData; status: ExpandStatus }) => void;
  setAdditionalJobDetails: (payload: { data?: AdditionalJobDetailsData; status: ExpandStatus }) => void;
  setContactInformation: (payload: { data?: ContactInformationData; status: ExpandStatus }) => void;
  setDesignConditions: (payload: { data: DesignConditionsData; status: ExpandStatus }) => void;
  setRepairType: (payload: { data: RepairTypeData; status: ExpandStatus }) => void;
  setEnclosureDetails: (payload: {
    data: EnclosureDetailsBaseData | EnclosureDetailsFlangeData;
    status: ExpandStatus;
  }) => void;
  setStrongbacks: (payload: { data: StrongbackMaterialData; status: ExpandStatus; addMore?: boolean }) => void;
  setStrongbackTypeLocation: (payload: { data: StrongbackTypeLocationData; status: ExpandStatus }) => void;
  setPostConstructionTestingRequirements: (payload: {
    data: PostConstructionTestingRequirementsData;
    status: ExpandStatus;
  }) => void;
  setSealingDetails: (payload: { data: SealingDetailsData; status: ExpandStatus }) => void;
  setDrawingNotes: (payload: { data: DrawingNotesData; status: ExpandStatus }) => void;
  setChecks: (payload: { data: ChecksBaseData | ChecksTeeData; status: ExpandStatus }) => void;
  setJointEfficiency: (payload: { data: JointEfficiencyData; status: ExpandStatus }) => void;
  setComponentDetails: (payload: { data: ComponentDetailsData; status: ExpandStatus }) => void;
  setDefectDetails: (payload: { data: DefectDetailsData; status: ExpandStatus }) => void;
  toggleExpandableSection: (section: MechanicalExpandableCards) => void;
  resetState: () => void;
  setDefaultValues: (data: CreateUpdateMechanicalJobSections) => void;
  setJobIdForDuplication: (id: string | null) => void;
};

const useCUMechanicalJobStore = create<CUMechanicalJobStore>((set) => ({
  ...initialState,
  setJobDetails: (payload) =>
    set((state) => {
      const additionalJobDetails: AdditionalJobDetailsSection =
        state.sections.additionalJobDetails.isDataFilled && state.sections.additionalJobDetails.status !== "blocked"
          ? state.sections.additionalJobDetails
          : {
              ...state.sections.additionalJobDetails,
              status: "open",
            };
      return {
        sections: {
          ...state.sections,
          jobDetails: { data: payload.data, isDataFilled: payload.status === "done", status: payload.status },
          additionalJobDetails,
        },
      };
    }),
  setAdditionalJobDetails: (payload) =>
    set((state) => {
      const contactInformation: ContactInformationSection =
        state.sections.contactInformation.isDataFilled && state.sections.contactInformation.status !== "blocked"
          ? state.sections.contactInformation
          : {
              ...state.sections.contactInformation,
              status: "open",
            };
      if (payload.data) {
        return {
          sections: {
            ...state.sections,
            additionalJobDetails: {
              data: payload.data,
              addMore: true,
              isDataFilled: payload.status === "done",
              status: payload.status,
            },
            contactInformation,
          },
        };
      }
      return {
        sections: {
          ...state.sections,
          additionalJobDetails: {
            ...state.sections.additionalJobDetails,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          contactInformation,
        },
      };
    }),
  setContactInformation: (payload) =>
    set((state) => {
      const designConditions: DesignConditionsSection =
        state.sections.designConditions.isDataFilled && state.sections.designConditions.status !== "blocked"
          ? state.sections.designConditions
          : {
              ...state.sections.designConditions,
              status: "open",
            };
      if (payload.data) {
        return {
          sections: {
            ...state.sections,
            contactInformation: {
              data: payload.data,
              addMore: true,
              isDataFilled: payload.status === "done",
              status: payload.status,
            },
            designConditions,
          },
        };
      }
      return {
        sections: {
          ...state.sections,
          contactInformation: {
            ...state.sections.contactInformation,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          designConditions,
        },
      };
    }),
  setDesignConditions: (payload) =>
    set((state) => {
      // Update status of next section
      const repairType = state.sections.repairType;
      const updatedRepairType =
        !repairType.isDataFilled || repairType.status === "blocked"
          ? { ...repairType, status: "open" as ExpandStatus }
          : repairType;

      return {
        sections: {
          ...state.sections,
          designConditions: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          repairType: updatedRepairType,
        },
      };
    }),

  setRepairType: (payload) =>
    set((state) => {
      // When repair type changes, we may need to update enclosure details and checks
      // based on the selected repair type
      const componentDetails = state.sections.componentDetails;

      // Update status of next section
      const updatedComponentDetails =
        !componentDetails.isDataFilled || componentDetails.status === "blocked"
          ? { ...componentDetails, status: "open" as ExpandStatus }
          : componentDetails;

      // Reset enclosure details data based on repair type
      const updatedEnclosureDetails = {
        ...state.sections.enclosureDetails,
        data:
          payload.data.repairType === "flange_clamp"
            ? {
                flange_clamp: "",
                stud_bolts: "",
                match_enclosure_to_component_material: undefined,
              }
            : {
                shells: "",
                endplates: "",
                ears: "",
                stud_bolts: "",
                riser_shells: "",
                match_enclosure_to_component_material: undefined,
              },
      };

      // Reset checks data based on repair type
      const updatedChecks = {
        ...state.sections.checks,
        data:
          payload.data.repairType === "tee"
            ? {
                is_the_service_hf_acid: undefined,
                is_the_service_wet_h2s: undefined,
                is_this_a_flare_line: undefined,
                does_the_line_vibrate_or_hammer: undefined,
                do_you_want_to_add_external_pressure_calculation: undefined,
                do_you_want_to_have_small_cross_section: undefined,
                do_you_want_to_add_area_of_reinforcement_calculation: false,
                do_you_need_minimum_clearance: undefined,
                min_id_clearance: null,
                min_id_clearance_2: null,
                do_you_want_to_add_mdmt_to_package: undefined,
                mdmt: null,
                is_this_a_redesign: undefined,
                is_this_revision: undefined,
                revision: null,
                do_you_wish_to_change_default_joint_efficiency: undefined,
                do_you_wish_to_change_default_checked_by: undefined,
                checked_by_first_name: null,
                checked_by_last_name: null,
              }
            : {
                is_the_service_hf_acid: undefined,
                is_the_service_wet_h2s: undefined,
                is_this_a_flare_line: undefined,
                does_the_line_vibrate_or_hammer: undefined,
                do_you_want_to_add_external_pressure_calculation: undefined,
                do_you_want_to_have_small_cross_section: undefined,
                do_you_need_minimum_clearance: undefined,
                min_id_clearance: null,
                do_you_want_to_add_mdmt_to_package: undefined,
                mdmt: null,
                is_this_a_redesign: undefined,
                is_this_revision: undefined,
                revision: null,
                do_you_wish_to_change_default_joint_efficiency: undefined,
                do_you_wish_to_change_default_checked_by: undefined,
                checked_by_first_name: null,
                checked_by_last_name: null,
              },
      };

      // Reset joint efficiency data
      const updatedJointEfficiency = {
        ...initialState.sections.jointEfficiency,
      };

      const updatedPostConstructionTestingRequirements = {
        ...initialState.sections.postConstructionTestingRequirements,
      };

      // Reset seal details because they depend on the selected repair/product type.
      const updatedSealingDetails = {
        ...state.sections.sealingDetails,
        data: {
          seal_preference: "",
          seal_type: "",
          seal_size: "",
          seal_material: "",
          sealant: "",
          sealant_2: "",
        },
        isDataFilled: false,
        status: "blocked" as ExpandStatus,
      };

      // Reset defectDetails data based on repair type
      const updatedDefectDetails = {
        ...state.sections.defectDetails,
        data: getInitialDefectDetails(payload.data.repairType),
        isDataFilled: false,
        status: "blocked" as ExpandStatus,
      };

      return {
        sections: {
          ...state.sections,
          repairType: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          enclosureDetails: updatedEnclosureDetails,
          checks: updatedChecks,
          jointEfficiency: updatedJointEfficiency,
          postConstructionTestingRequirements: updatedPostConstructionTestingRequirements,
          componentDetails: updatedComponentDetails,
          defectDetails: updatedDefectDetails,
          sealingDetails: updatedSealingDetails,
        },
      };
    }),

  setEnclosureDetails: (payload) =>
    set((state) => {
      const shouldSkipStrongbackSections = state.sections.repairType.data.repairType === "long_radius";
      const strongbacks = state.sections.strongbacks;
      const strongbackTypeLocation = state.sections.strongbackTypeLocation;
      const postConstructionTestingRequirements = state.sections.postConstructionTestingRequirements;

      const updatedStrongbacks = shouldSkipStrongbackSections
        ? { ...initialState.sections.strongbacks }
        : !strongbacks.isDataFilled || strongbacks.status === "blocked"
          ? { ...strongbacks, status: "open" as ExpandStatus }
          : strongbacks;

      const updatedStrongbackTypeLocation = shouldSkipStrongbackSections
        ? { ...initialState.sections.strongbackTypeLocation }
        : !strongbackTypeLocation.isDataFilled || strongbackTypeLocation.status === "blocked"
          ? { ...strongbackTypeLocation, status: "blocked" as ExpandStatus }
          : strongbackTypeLocation;

      const updatedPostConstructionTestingRequirements = shouldSkipStrongbackSections
        ? !postConstructionTestingRequirements.isDataFilled || postConstructionTestingRequirements.status === "blocked"
          ? { ...postConstructionTestingRequirements, status: "open" as ExpandStatus }
          : postConstructionTestingRequirements
        : postConstructionTestingRequirements;

      return {
        sections: {
          ...state.sections,
          enclosureDetails: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          strongbacks: updatedStrongbacks,
          strongbackTypeLocation: updatedStrongbackTypeLocation,
          postConstructionTestingRequirements: updatedPostConstructionTestingRequirements,
        },
      };
    }),

  setStrongbacks: (payload) =>
    set((state) => {
      const addMore = payload.addMore ?? true;
      const strongbackTypeLocation = state.sections.strongbackTypeLocation;
      const postConstructionTestingRequirements = state.sections.postConstructionTestingRequirements;

      // If addMore is false (user chose not to add strongbacks), skip strongbackTypeLocation and open postConstructionTestingRequirements
      const shouldSkipStrongbackTypeLocation = !addMore && payload.status === "done";

      const updatedStrongbackTypeLocation = shouldSkipStrongbackTypeLocation
        ? { ...strongbackTypeLocation, status: "blocked" as ExpandStatus }
        : payload.status !== "blocked" &&
            (!strongbackTypeLocation.isDataFilled || strongbackTypeLocation.status === "blocked")
          ? { ...strongbackTypeLocation, status: "open" as ExpandStatus }
          : strongbackTypeLocation;

      const updatedPostConstructionTestingRequirements = shouldSkipStrongbackTypeLocation
        ? !postConstructionTestingRequirements.isDataFilled || postConstructionTestingRequirements.status === "blocked"
          ? { ...postConstructionTestingRequirements, status: "open" as ExpandStatus }
          : postConstructionTestingRequirements
        : postConstructionTestingRequirements;

      return {
        sections: {
          ...state.sections,
          strongbacks: {
            data: payload.data
              ? payload.data
              : {
                  strongbacks: "",
                  strongbacks_stud_bolts: "",
                  match_strongback_to_enclosure: undefined,
                },
            isDataFilled: payload.status === "done",
            status: payload.status,
            addMore,
          },
          strongbackTypeLocation: updatedStrongbackTypeLocation,
          postConstructionTestingRequirements: updatedPostConstructionTestingRequirements,
        },
      };
    }),
  setStrongbackTypeLocation: (payload) =>
    set((state) => {
      const postConstructionTestingRequirements = state.sections.postConstructionTestingRequirements;
      const shouldOpenNext = payload.status === "done";
      const dataWithDefaults = {
        ...payload.data,
        strongbacks: payload.data.strongbacks?.length
          ? payload.data.strongbacks
          : initialState.sections.strongbackTypeLocation.data.strongbacks,
      };
      const updatedPostConstructionTestingRequirements =
        shouldOpenNext &&
        (!postConstructionTestingRequirements.isDataFilled || postConstructionTestingRequirements.status === "blocked")
          ? { ...postConstructionTestingRequirements, status: "open" as ExpandStatus }
          : postConstructionTestingRequirements;

      return {
        sections: {
          ...state.sections,
          strongbackTypeLocation: {
            data: dataWithDefaults,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          postConstructionTestingRequirements: updatedPostConstructionTestingRequirements,
        },
      };
    }),

  setPostConstructionTestingRequirements: (payload) =>
    set((state) => {
      const sealingDetails = state.sections.sealingDetails;
      const updatedSealingDetails =
        !sealingDetails.isDataFilled || sealingDetails.status === "blocked"
          ? { ...sealingDetails, status: "open" as ExpandStatus }
          : sealingDetails;

      return {
        sections: {
          ...state.sections,
          postConstructionTestingRequirements: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          sealingDetails: updatedSealingDetails,
        },
      };
    }),

  setSealingDetails: (payload) =>
    set((state) => {
      // Update status of next section
      const drawingNotes = state.sections.drawingNotes;
      const updatedDrawingNotes =
        !drawingNotes.isDataFilled || drawingNotes.status === "blocked"
          ? { ...drawingNotes, status: "open" as ExpandStatus }
          : drawingNotes;

      return {
        sections: {
          ...state.sections,
          sealingDetails: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          drawingNotes: updatedDrawingNotes,
        },
      };
    }),
  setComponentDetails: (payload) =>
    set((state) => {
      const shouldChangeDefectDetails =
        state.sections.componentDetails.data.components_data.length !== payload.data.components_data.length;

      const defectDetails: DefectDetailsSection = state.sections.defectDetails;

      let updatedDefectDetails = defectDetails;

      if (!defectDetails.isDataFilled || defectDetails.status === "blocked") {
        updatedDefectDetails = { ...updatedDefectDetails, status: "open" as ExpandStatus };
      }

      if (shouldChangeDefectDetails) {
        const currentRepairType = state.sections.repairType.data.repairType;
        updatedDefectDetails = {
          ...updatedDefectDetails,
          status: "open" as ExpandStatus,
          isDataFilled: false,
          data: getInitialDefectDetails(currentRepairType),
        };
      }

      return {
        sections: {
          ...state.sections,
          componentDetails: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          defectDetails: updatedDefectDetails,
        },
      };
    }),
  setDefectDetails: (payload) =>
    set((state) => {
      // Update status of next section
      const enclosureDetails = state.sections.enclosureDetails;
      const updatedEnclosureDetails =
        !enclosureDetails.isDataFilled || enclosureDetails.status === "blocked"
          ? { ...enclosureDetails, status: "open" as ExpandStatus }
          : enclosureDetails;

      return {
        sections: {
          ...state.sections,
          defectDetails: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          enclosureDetails: updatedEnclosureDetails,
        },
      };
    }),
  setDrawingNotes: (payload) =>
    set((state) => {
      // Update status of next section
      const checks = state.sections.checks;
      const updatedChecks =
        !checks.isDataFilled || checks.status === "blocked" ? { ...checks, status: "open" as ExpandStatus } : checks;

      return {
        sections: {
          ...state.sections,
          drawingNotes: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          checks: updatedChecks,
        },
      };
    }),

  setChecks: (payload) =>
    set((state) => {
      // Check if joint efficiency section should be opened
      const shouldOpenJointEfficiency = payload.data.do_you_wish_to_change_default_joint_efficiency === true;

      let nextSection: JointEfficiencySection | typeof state.sections.finalDesign;

      if (shouldOpenJointEfficiency) {
        const jointEfficiency = state.sections.jointEfficiency;
        nextSection =
          !jointEfficiency.isDataFilled || jointEfficiency.status === "blocked"
            ? { ...jointEfficiency, status: "open" as ExpandStatus }
            : jointEfficiency;
      } else {
        const finalDesign = state.sections.finalDesign;
        nextSection =
          !finalDesign.isDataFilled || finalDesign.status === "blocked"
            ? { ...finalDesign, status: "open" as ExpandStatus }
            : finalDesign;
      }

      return {
        sections: {
          ...state.sections,
          checks: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          ...(shouldOpenJointEfficiency
            ? { jointEfficiency: nextSection as JointEfficiencySection }
            : { finalDesign: nextSection as typeof state.sections.finalDesign }),
        },
      };
    }),

  setJointEfficiency: (payload) =>
    set((state) => {
      // Update status of next section
      const finalDesign = state.sections.finalDesign;
      const updatedFinalDesign =
        !finalDesign.isDataFilled || finalDesign.status === "blocked"
          ? { ...finalDesign, status: "open" as ExpandStatus }
          : finalDesign;

      return {
        sections: {
          ...state.sections,
          jointEfficiency: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          finalDesign: updatedFinalDesign,
        },
      };
    }),

  toggleExpandableSection: (section) =>
    set((state) => {
      const selectedStatus = state.sections[section];
      const previousState = selectedStatus?.status;
      let newSectionStatus = previousState === "open" ? "canBeOpened" : "open";
      if (newSectionStatus === "canBeOpened" && selectedStatus?.isDataFilled) {
        newSectionStatus = "done";
      }
      return {
        sections: {
          ...state.sections,
          [section]: {
            ...state.sections[section],
            status: newSectionStatus,
          },
        },
      };
    }),
  resetState: () =>
    set(() => {
      return initialState;
    }),
  setDefaultValues: (payload: CreateUpdateMechanicalJobSections) =>
    set(() => {
      return { sections: payload };
    }),
  setJobIdForDuplication: (payload) =>
    set((state) => {
      return { ...state, jobIdForDuplication: payload };
    }),
}));

export default useCUMechanicalJobStore;
